import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { ThirdPartyService } from '@ruby/core/http/third-party/third-party.service';
import { IContact } from '@ruby/modules/account/models/contact.interface';
import { IThirdPartyLOA, IThirdPartyLOAStatus, IThirdPartyTemplate } from '@ruby/modules/third-party/models/third-party.interface';
import * as ThirdPartyActions from '@ruby/modules/third-party/store/actions/third-party-core.actions';
import { ThirdPartyAuthorizationActions } from '@ruby/modules/third-party/store/third-party-authorization.index';
import { ThirdPartyCompaniesActions } from '@ruby/modules/third-party/store/third-party-companies.index';
import { ThirdPartyStepperActions } from '@ruby/modules/third-party/store/third-party-stepper.index';
import { ERole } from '@ruby/shared/enums/roles.enums';
import { IError } from '@ruby/shared/models/request/error.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';
import { hasRole } from '@ruby/store/selectors/user-account.selectors';

@Injectable()
export class ThirdPartyCoreEffects {
  getLoas$ = createEffect(() => this.actions$.pipe(
    ofType(
      ThirdPartyActions.loadLOA,
      ThirdPartyActions.updateLOAStatusSuccess
    ),
    concatMap(action => of(action).pipe(
      withLatestFrom(this.store.select(hasRole(ERole.ROLE_3PL)))
    )),
    exhaustMap(([action, is3PL]) =>
      (is3PL ? this.thirdPartySvc.getThirdPartyTPLUserLOAs() : this.thirdPartySvc.getThirdPartyUserLOAs()).pipe(
        map((loas: Array<IThirdPartyLOA>) => ThirdPartyActions.loadLOASuccess({ data: loas })),
        catchError((error: IError) => of(ThirdPartyActions.loadLOAFailure({ error })))
      ))
  ));

  getContract$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.loadContract),
    exhaustMap(action => this.thirdPartySvc.getLOAById(action.data).pipe(
      map((loa: IThirdPartyLOA) => ThirdPartyActions.loadContractSuccess({ data: loa })),
      catchError((error: IError) => of(ThirdPartyActions.loadContractFailure({ error })))
    ))
  ));

  getStatus$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.loadLOAStatus),
    concatMap(action => of(action).pipe(
      withLatestFrom(this.store.select(hasRole(ERole.ROLE_3PL)))
    )),
    exhaustMap(([action, is3PL]) =>
      (is3PL ? this.thirdPartySvc.getLOAStatusForTplUser() : this.thirdPartySvc.getLOAStatus()).pipe(
        map((status: IThirdPartyLOAStatus) => ThirdPartyActions.loadLOAStatusSuccess({ data: status })),
        catchError((error: IError) => of(ThirdPartyActions.loadLOAStatusFailure({ error })))
      ))
  ));

  get3PLContactDetails$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.loadContactDetails),
    exhaustMap(action => this.thirdPartySvc.getContactDetails(action.data).pipe(
      map((contact: IContact) => ThirdPartyActions.loadContactDetailsSuccess({ data: contact })),
      catchError((error: IError) => of(ThirdPartyActions.loadContactDetailsFailure({ error })))
    ))
  ));

  updateLOAStatus$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.updateLOAStatus),
    exhaustMap((action) => this.thirdPartySvc.updateLOAStatus(action.data).pipe(
      map((status: IThirdPartyLOA) => ThirdPartyActions.updateLOAStatusSuccess({ data: status })),
      catchError((error: IError) => {
        this.utilsSvc.showErrorToastr(error.title, error.message);
        return of(ThirdPartyActions.updateLOAStatusFailure({ error }));
      })
    ))
  ));

  getThirdPartyTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.loadTemplate),
    exhaustMap(() => this.thirdPartySvc.getTemplate().pipe(
      map((template: IThirdPartyTemplate) => ThirdPartyActions.loadTemplateSuccess({ data: template })),
      catchError((error: IError) => of(ThirdPartyActions.loadTemplateFailure({ error })))
    ))
  ));

  signLOA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyActions.signLOA),
      exhaustMap(action =>
        this.thirdPartySvc.sign(action.data).pipe(
          map((contract: IThirdPartyLOA) => ThirdPartyActions.signLOASuccess({ data: contract })),
          catchError((error: IError) => {
            this.utilsSvc.showErrorToastr(error.title, error.message);
            return of(ThirdPartyActions.signLOAFailure({ error }));
          })
        )
      )
    )
  );

  updateLOA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ThirdPartyActions.updateLOA),
      exhaustMap(action =>
        this.thirdPartySvc.updateLOA(action.data).pipe(
          map((contract: IThirdPartyLOA) => ThirdPartyActions.updateLOASuccess({ data: contract })),
          catchError((error: IError) => {
            this.utilsSvc.showErrorToastr(error.title, error.message);
            return of(ThirdPartyActions.updateLOAFailure({ error }));
          })
        )
      )
    )
  );

  loadLob$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.signLOASuccess),
    map(action => ThirdPartyAuthorizationActions.loadAvailableLineOfBusiness({
        companyEId: action.data.companyEid,
        tplCompanyEId: action.data.companyTPLEid,
        afterSign: true
      })
    ))
  );

  enableDoneStep$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyAuthorizationActions.loadAvailableLineOfBusinessSuccess),
    tap(action => {
        if (action.data.length > 0 && action.afterSign) {
          this.store.dispatch(ThirdPartyStepperActions.enableDone({ data: true }));
          const subscriberId = timer(100).pipe(
            take(1)
          ).subscribe(() => {
            this.store.dispatch(ThirdPartyStepperActions.setStepIndex({ data: 4 }));
            subscriberId.unsubscribe();
          });
        } else if (action.afterSign) {
          this.utilsSvc.showSuccessToastr('LOA Created', 'LOA has been created successfully.');
          this.router.navigate(['home/third-party-providers']);
          this.store.dispatch(ThirdPartyActions.hardReset({ data: true }));
        } else if (action.data.length === 0 && !action.afterSign) {
          this.utilsSvc.showWarnToastr(
            'Invalid Company Provider',
            'The selected company has an LOA in place, please select a different company',
            {
              closeButton: true,
              timeOut: 150000,
              titleClass: 'text-dark font-weight-bold',
              messageClass: 'text-dark font-weight-bold'
            }
          );
          this.store.dispatch(ThirdPartyCompaniesActions.clearCompanyProvider());
        }
      }
    )), { dispatch: false }
  );

  hardReset$ = createEffect(() => this.actions$.pipe(
    ofType(ThirdPartyActions.hardReset),
    tap(action => {
        if (action.data) {
          this.store.dispatch(ThirdPartyCompaniesActions.clearCompanies());
          this.store.dispatch(ThirdPartyAuthorizationActions.clear());
          this.store.dispatch(ThirdPartyStepperActions.clear());
        } else {
          this.store.dispatch(ThirdPartyAuthorizationActions.clearApplications());
        }
      }
    )), { dispatch: false }
  );

  openDialogUpdate$ = createEffect(() => this.actions$.pipe(
      ofType(ThirdPartyActions.updateLOASuccess),
      tap(action => {
        this.utilsSvc.showSuccessToastr('LOA Updated', 'LOA has been updated successfully.');
        this.router.navigate(['home/third-party-providers']);
        this.store.dispatch(ThirdPartyActions.hardReset({ data: true }));
      })),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private thirdPartySvc: ThirdPartyService,
    private utilsSvc: UtilsService,
    private router: Router,
    private store: Store
  ) {}
}
